import type { Session } from '@supabase/supabase-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { EventType } from '../poller/types.js';
import { checkAuthorSite, refreshWatchable } from './client.ts';
import {
  loadEvents,
  loadLastSeen,
  loadWatchlist,
  markSeen,
  markSiteSeen,
  setConsumed,
  setEventTypes,
  setPreferences,
  touchLastSeen,
  unsubscribe,
} from './data.ts';
import { MailSetting } from './components/MailSetting.tsx';
import type { WatchEvent, WatchItem } from './lib/model.js';
import { About } from './screens/About.tsx';
import { Add, resultKey, type FacetId } from './screens/Add.tsx';
import { Detail } from './screens/Detail.tsx';
import { Home } from './screens/Home.tsx';
import { SignIn } from './screens/SignIn.tsx';
import { Slate } from './screens/Slate.tsx';
import { supabase } from './supabase.ts';
import { PalettePicker } from './theme.tsx';

type Tab = 'home' | 'slate' | 'add' | 'settings' | 'about';

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) return <div className="empty" />;
  if (!session) return <SignIn />;
  return <SignedIn userId={session.user.id} email={session.user.email ?? null} />;
}

function SignedIn({ userId, email }: { userId: string; email: string | null }) {
  const [tab, setTab] = useState<Tab>('home');
  const [openId, setOpenId] = useState<string | null>(null);
  const [items, setItems] = useState<WatchItem[]>([]);
  const [events, setEvents] = useState<WatchEvent[]>([]);
  const [lastSeenAt, setLastSeenAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Held here rather than in `Add`: opening a followed row swaps the whole tab
  // body for its item page, so `Add` unmounts and anything it held goes with
  // it. Coming back to a search box you had typed into and emptied is worse
  // than the repeat search this costs.
  const [query, setQuery] = useState('');
  const [facet, setFacet] = useState<FacetId>('all');

  // Frozen for the session so a long-lived tab doesn't quietly reclassify
  // things underneath the reader as midnight passes.
  const now = useMemo(() => new Date(), []);

  const refresh = useCallback(async () => {
    try {
      const [nextItems, nextEvents] = await Promise.all([loadWatchlist(), loadEvents()]);
      setItems(nextItems);
      setEvents(nextEvents);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // The seam is drawn from the *previous* visit, so read it before the
    // visit is recorded — otherwise it always says "nothing new".
    loadLastSeen(userId)
      .then(setLastSeenAt)
      .catch(() => setLastSeenAt(null));
    void refresh();
  }, [userId, refresh]);

  const openItem = items.find((i) => i.watchableId === openId) ?? null;

  /**
   * Marks the changes seen and moves the seam forward — but only when the
   * user actually leaves the changes view, not on every render. Doing it on
   * load would clear the list out from under them mid-read.
   */
  const settle = useCallback(async () => {
    const unseen = events.filter((e) => !e.seen).map((e) => e.id);
    if (unseen.length === 0) return;
    try {
      await markSeen(unseen, userId);
      await touchLastSeen(userId);
      setEvents((current) => current.map((e) => ({ ...e, seen: true })));
    } catch {
      // Losing the read-state is annoying, not broken. It'll retry next time.
    }
  }, [events, userId]);

  function go(next: Tab) {
    // The changes are on Home now, so that is the view whose *leaving* marks
    // them seen. Doing it on arrival would clear the list mid-read.
    if (tab === 'home' && next !== 'home') void settle();
    setOpenId(null);
    setTab(next);
  }

  // Its own screen rather than a fifth tab: the tab bar answers "where am I
  // working", and About is read once. It keeps Settings lit while it is open
  // so the way back is where it was left.
  if (tab === 'about') {
    return (
      <div className="app">
        <About onBack={() => go('settings')} />
        <TabBar tab={tab} onChange={go} />
      </div>
    );
  }

  if (openItem) {
    const subscriptionTypes: EventType[] = [];
    // The series a book belongs to, but only if it is on this reader's slate —
    // otherwise "Book 3 of X" is a fact to state, not a link to offer.
    const partOf = openItem.details?.partOf ?? null;
    const seriesOnSlate = partOf
      ? (items.find(
          (i) =>
            i.kind === 'series' && i.source === partOf.source && i.sourceId === partOf.sourceId,
        ) ?? null)
      : null;
    return (
      <div className="app">
        <Detail
          item={openItem}
          events={events}
          now={now}
          eventTypes={subscriptionTypes}
          onBack={() => setOpenId(null)}
          onToggleConsumed={async (consumed) => {
            await setConsumed(openItem.subscriptionId, consumed).catch(() => {});
            await refresh();
            setOpenId(null);
          }}
          onSetEventTypes={async (types) => {
            await setEventTypes(openItem.subscriptionId, types).catch(() => {});
            await refresh();
          }}
          onSetPreferences={async (preferences) => {
            await setPreferences(openItem.subscriptionId, preferences).catch(() => {});
            await refresh();
          }}
          onRemove={async () => {
            await unsubscribe(openItem.subscriptionId).catch(() => {});
            setOpenId(null);
            await refresh();
          }}
          seriesOnSlate={seriesOnSlate}
          onOpenItem={(item) => setOpenId(item.watchableId)}
          onSetSite={async (url) => {
            const site = await checkAuthorSite(openItem.watchableId, url);
            await refresh();
            return site;
          }}
          onCheckSite={() => checkAuthorSite(openItem.watchableId)}
          onSiteSeen={() => {
            // Reloaded afterwards on purpose. The component hides the mark
            // itself, but that state dies when the screen closes — without the
            // reload the dot is back on the next visit, having been cleared in
            // the database, which looks exactly like a mark that will not go
            // away.
            void markSiteSeen(openItem.subscriptionId)
              .then(() => refresh())
              .catch(() => {});
          }}
          onRefresh={async () => {
            try {
              const result = await refreshWatchable(openItem.watchableId);
              await refresh();
              // Says what happened either way: a silent no-op looks like a
              // button that does not work.
              return result.refreshed ? null : `Already ${result.reason ?? 'up to date'}.`;
            } catch (e) {
              return e instanceof Error ? e.message : 'Could not refresh that.';
            }
          }}
        />
        <TabBar tab={tab} onChange={go} />
      </div>
    );
  }

  return (
    <div className="app">
      {tab !== 'add' && (
        <div className="appbar">
          <span className="wordmark">slate</span>
          <div className="appbar-actions">
            <button className="icon-btn" aria-label="Add something" onClick={() => go('add')}>
              +
            </button>
          </div>
        </div>
      )}

      {error && <p className="notice">{error}</p>}

      {(tab === 'home' || tab === 'slate') &&
        (loading ? (
          <div className="empty">
            <p>Loading your list…</p>
          </div>
        ) : tab === 'home' ? (
          <Home
            items={items}
            events={events}
            lastSeenAt={lastSeenAt}
            now={now}
            onOpen={(item) => setOpenId(item.watchableId)}
            onAdd={() => go('add')}
            onOpenSlate={() => go('slate')}
          />
        ) : (
          <Slate
            items={items}
            events={events}
            lastSeenAt={lastSeenAt}
            now={now}
            onOpen={(item) => setOpenId(item.watchableId)}
            onAdd={() => go('add')}
          />
        ))}

      {tab === 'add' && (
        <>
          <div className="appbar">
            <span className="wordmark">add</span>
            <div className="appbar-actions">
              <button className="icon-btn" aria-label="Close" onClick={() => go('slate')}>
                ×
              </button>
            </div>
          </div>
          <Add
            items={items}
            query={query}
            onQuery={setQuery}
            facet={facet}
            onFacet={setFacet}
            onOpenItem={(item) => setOpenId(item.watchableId)}
            onAdded={refresh}
          />
        </>
      )}

      {tab === 'settings' && (
        <>
          <div className="verdict">
            <h1>Settings</h1>
          </div>
          <div className="section">
            <div className="section-head">
              <span className="label">Theme</span>
            </div>
          </div>
          <PalettePicker />
          <MailSetting userId={userId} email={email} />
          <button
            className="wide-action"
            style={{ marginTop: 28, marginBottom: 0 }}
            onClick={() => go('about')}
          >
            About slate
          </button>
          <button className="danger" onClick={() => supabase.auth.signOut()}>
            Sign out
          </button>
          <p className="attribution">
            This product uses the TMDb API but is not endorsed or certified by TMDb. Book data
            from Hardcover.
          </p>
        </>
      )}

      <TabBar tab={tab} onChange={go} />
    </div>
  );
}

function TabBar({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  return (
    <nav className="tabbar">
      <button className={tab === 'home' ? 'on' : ''} onClick={() => onChange('home')}>
        Home
      </button>
      <button className={tab === 'slate' ? 'on' : ''} onClick={() => onChange('slate')}>
        Slate
      </button>
      <button className={tab === 'add' ? 'on' : ''} onClick={() => onChange('add')}>
        Add
      </button>
      <button
        className={tab === 'settings' || tab === 'about' ? 'on' : ''}
        onClick={() => onChange('settings')}
      >
        Settings
      </button>
    </nav>
  );
}

export { resultKey };
